<script setup lang="ts">
import { faXmark } from '@fortawesome/pro-light-svg-icons'
import { MARKING_COLORS, markingColorStyle } from '~/utils/markings'

const {
  activeMarking,
  isEditing,
  draftNote,
  tooltipCoords,
  tooltipRef,
  textareaRef,
  hasSavedNote,
  formatTimestamp,
  closeTooltip,
  handleColorClick,
  handleSave,
  handleTextareaKeydown,
  handleEditStart,
  handleDelete,
} = useMarkingNoteTooltip()
</script>

<template>
  <div
    v-if="activeMarking"
    ref="tooltipRef"
    class="fixed z-[69] flex flex-col bg-white reading-dark:bg-carbon-500 rounded-xl shadow-md select-none p-xs gap-2xs w-[min(320px,calc(100vw-16px))] max-h-[calc(100dvh-16px)] overflow-y-auto"
    :style="{
      top: `${tooltipCoords.y}px`,
      left: `${tooltipCoords.x}px`,
    }"
  >
    <div class="flex shrink-0 items-center justify-between gap-s">
      <div v-if="!isEditing" class="flex min-w-0 items-center gap-xs pl-xs">
        <span
          class="size-5.5 shrink-0 rounded-full border-2"
          :class="[
            markingColorStyle(activeMarking.color).buttonClass,
            markingColorStyle(activeMarking.color).borderColorClass,
          ]"
          aria-hidden="true"
        />
        <span
          class="font-body text-sm text-carbon-300 reading-dark:text-carbon-50 whitespace-nowrap"
        >
          {{ formatTimestamp(activeMarking.createdAt) }}
        </span>
      </div>
      <div v-else class="flex items-center gap-1.5">
        <MarkingsColorSwatch
          v-for="color in MARKING_COLORS"
          :key="color"
          :color="color"
          :label="`Endre farge til ${color}`"
          :selected="activeMarking.color === color"
          @click="handleColorClick(color)"
        />
      </div>
      <ControlButton
        size="small"
        type="ghost"
        pinned
        :toggle-selected="false"
        :icon="faXmark"
        class="size-8 shrink-0 bg-carbon-50 text-blurple-500 reading-dark:bg-carbon-400 reading-dark:text-carbon-50 reading-dark:hover:bg-carbon-300 reading-dark:focus-visible:bg-carbon-300"
        aria-label="Lukk notat"
        @click="closeTooltip"
      />
    </div>

    <template v-if="!isEditing">
      <!-- Notater kan være opptil MARKING_NOTE_MAX tegn. Uten en grense her
           vokser dialogen med notatet og havner utenfor skjermen. -->
      <p
        class="font-body text-sm text-neutral-black reading-dark:text-carbon-50 whitespace-pre-wrap break-words p-xs max-h-[40dvh] overflow-y-auto"
      >
        {{ activeMarking.note?.trim() || '' }}
      </p>

      <div class="flex items-center justify-end gap-2xs pt-2xs">
        <Button
          size="xsmall"
          variant="tertiary"
          class="reading-dark:text-carbon-50 reading-dark:enabled:hover:bg-surface-secondary reading-dark:disabled:text-carbon-300 reading-dark:focus-visible:ring-offset-carbon-500"
          color="neutral"
          shape="square"
          :disabled="!hasSavedNote"
          @click="handleDelete"
        >
          Slett
        </Button>
        <Button
          size="xsmall"
          variant="primary"
          class="reading-dark:focus-visible:ring-offset-carbon-500"
          color="neutral"
          shape="square"
          @click.stop="handleEditStart"
        >
          Rediger
        </Button>
      </div>
    </template>

    <template v-else>
      <label for="marking-note-textarea" class="sr-only">Notat</label>
      <textarea
        id="marking-note-textarea"
        ref="textareaRef"
        v-model="draftNote"
        rows="8"
        placeholder="Skriv notat her..."
        class="bg-carbon-50 reading-dark:bg-carbon-400 text-neutral-black placeholder:text-neutral-black reading-dark:text-carbon-50 reading-dark:placeholder:text-carbon-50 rounded-lg p-xs font-body font-normal text-sm leading-normal h-48 shrink-0 resize-none border-0 outline-none"
        @keydown="handleTextareaKeydown"
      />

      <div class="flex items-center justify-end gap-2xs">
        <Button
          size="xsmall"
          variant="tertiary"
          class="reading-dark:text-carbon-50 reading-dark:enabled:hover:bg-surface-secondary reading-dark:disabled:text-carbon-300 reading-dark:focus-visible:ring-offset-carbon-500"
          color="neutral"
          shape="square"
          :disabled="!hasSavedNote"
          @click="handleDelete"
        >
          Slett
        </Button>
        <Button
          size="xsmall"
          variant="primary"
          class="reading-dark:focus-visible:ring-offset-carbon-500"
          color="neutral"
          shape="square"
          @click.stop="handleSave"
        >
          Lagre
        </Button>
      </div>
    </template>
  </div>
</template>
